import { Outlet, createFileRoute, useNavigate } from '@tanstack/react-router'
import { IntegrationIcon } from '~/features/integrations/components'
import { Icon, type MenuItem, DropdownMenu } from '~/elements'
import { integrationQueryOptions, listWorkspaceIntegrationsQueryOptions } from '~/queries'
import { useCurrentRouteId } from '~/hooks'
import { PrivateIntegrationInstallPopover } from '~/features/integrations/components/PrivateIntegrationInstallPopover'
import { ChevronDown, Settings, List, Tag, ShieldCheck } from 'lucide-react'
import { useSuspenseQuery } from '@tanstack/react-query'
import _ from 'lodash'
import { setTag } from '@sentry/react'
import { isBotpressWorkspace } from '~/utils'
import type { ComponentProps } from 'react'
import { Page } from '~/components'
import { Badge, Button } from '@bpinternal/ui-kit-next'

export const Route = createFileRoute('/workspaces/$workspaceId/integrations/$integrationId')({
  loader: ({ context, params: { integrationId, workspaceId } }) =>
    Promise.all([
      context.queryClient.ensureQueryData(listWorkspaceIntegrationsQueryOptions({ workspaceId })),
      context.queryClient.ensureQueryData(integrationQueryOptions({ integrationId, workspaceId })),
    ]),
  component: () => <IntegrationComponent />,
})

const IntegrationComponent = () => {
  const currentRouteId = useCurrentRouteId()
  const { integrationId, workspaceId } = Route.useParams()
  const integration = useSuspenseQuery(integrationQueryOptions({ integrationId, workspaceId })).data
  const nVersions = useSuspenseQuery(listWorkspaceIntegrationsQueryOptions({ workspaceId })).data.filter(
    (i) => i.name === integration.name
  ).length
  const integrations = _(useSuspenseQuery(listWorkspaceIntegrationsQueryOptions({ workspaceId })).data)
    .groupBy('name')
    .mapValues((ints) => ints[0]!)
    .values()
    .value()

  const navigate = useNavigate({ from: Route.fullPath })

  setTag('integrationId', integrationId)

  const items: ComponentProps<typeof Page>['navbarItems'] = [
    {
      children: 'Details',
      to: '/workspaces/$workspaceId/integrations/$integrationId',
      active: currentRouteId === '/workspaces/$workspaceId/integrations/$integrationId/',
    },
    {
      leading: <Icon color="gray" icon={List} />,
      children: 'Logs',
      to: '/workspaces/$workspaceId/integrations/$integrationId/logs',
      active: currentRouteId === '/workspaces/$workspaceId/integrations/$integrationId/logs',
    },
    {
      leading: <Icon color="gray" icon={Tag} />,
      children: `${nVersions > 1 ? nVersions : ''} Versions`,
      to: '/workspaces/$workspaceId/integrations/$integrationId/versions',
      active: currentRouteId === '/workspaces/$workspaceId/integrations/$integrationId/versions',
    },
    {
      children: 'Settings',
      leading: <Icon color="gray" icon={Settings} />,
      to: '/workspaces/$workspaceId/integrations/$integrationId/settings',
      active: currentRouteId === '/workspaces/$workspaceId/integrations/$integrationId/settings',
    },
  ]

  const integrationSelectMenuItems: MenuItem[] = integrations
    .map(({ name, id, iconUrl }) => ({
      type: 'item' as const,
      content: name,
      leadingIcon: <IntegrationIcon className="size-4" iconUrl={iconUrl} />,
      onSelect: () => void navigate({ to: currentRouteId as any, params: { integrationId: id } }),
    }))
    .sort((a, b) => a.content.localeCompare(b.content))

  return (
    <Page
      title={
        <DropdownMenu align="start" className="min-w-52" content={integrationSelectMenuItems}>
          <Button variant="ghost" className="text-gray-12">
            <div className="flex items-center gap-2">
              <IntegrationIcon className="size-7" iconUrl={integration.iconUrl} />
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold">{integration.name}</span>
                <span className="text-xs font-normal italic text-gray-9">{integration.version}</span>
              </div>
              {integration.verificationStatus === 'approved' ||
                (isBotpressWorkspace(workspaceId) && (
                  <Badge
                    variant="default"
                    className="ml-2 border-grass-3 bg-grass-3 text-grass-11 shadow-none hover:bg-grass-3"
                  >
                    Verified <ShieldCheck className="ml-1 h-3 w-3" />
                  </Badge>
                ))}
              <ChevronDown className="h-4 w-4 text-gray-9" />
            </div>
          </Button>
        </DropdownMenu>
      }
      actions={
        integration ? (
          <PrivateIntegrationInstallPopover currentIntegration={integration} workspaceId={workspaceId}>
            <Button>
              Install
              <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </PrivateIntegrationInstallPopover>
        ) : null
      }
      navbarItems={items}
    >
      <Outlet />
    </Page>
  )
}
